import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
// Import icons - you may need to install these packages
// npm install react-icons
// Or use another icon library you prefer
import { HiMail, HiLockClosed, HiUser, HiLogin, HiUserAdd } from 'react-icons/hi';
import { sanitizeInput } from '../utils/sanitize';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showResendButton, setShowResendButton] = useState(false);
  
  // Check for query parameters on load (for email confirmation)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const error = queryParams.get('error');
    const message = queryParams.get('message');
    
    if (message === 'Email confirmation successful') {
      setSuccessMessage('Email confirmed successfully! You can now log in with your credentials.');
      // Clear any previous error about unverified email
      setErrorMessage('');
      setShowResendButton(false);
    }
    
    if (error) {
      setErrorMessage(decodeURIComponent(error));
    }
  }, [location]);
  
  // If user is already logged in, redirect to dashboard
  useEffect(() => {
    const checkUser = async () => {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.user) {
        console.log("User is logged in, redirecting to dashboard");
        navigate('/dashboard');
      }
    };
    
    checkUser();
  }, [navigate]);
  
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    // Sanitize inputs
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedPassword = sanitizeInput(password);
    
    if (!sanitizedEmail || !sanitizedPassword) {
      setErrorMessage('Please enter both email and password');
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      if (activeTab === 'login') {
        // Login flow
        const { data, error } = await supabase.auth.signInWithPassword({ 
          email: sanitizedEmail, 
          password: sanitizedPassword 
        });
        
        if (error) {
          console.error("Login error:", error.message);
          
          // Handle specific error cases
          if (error.message.includes('Email not confirmed')) {
            setErrorMessage('Your email is not yet verified. Please check your inbox for a confirmation link.');
            setShowResendButton(true);
          } else if (error.message.includes('Invalid login credentials')) {
            // We can't check if email is verified without admin access,
            // so we'll just show a generic message
            setErrorMessage('Invalid email or password. If you recently signed up, please verify your email first.');
            setShowResendButton(true);
          } else {
            setErrorMessage(error.message);
          }
        } else if (data?.user) {
          console.log("Login successful, redirecting to dashboard");
          navigate('/dashboard');
        }
      } else {
        // Signup flow
        const { data, error } = await supabase.auth.signUp({ 
          email: sanitizedEmail, 
          password: sanitizedPassword,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`
          }
        });
        
        if (error) throw error;
        
        if (data?.user?.identities?.length === 0) {
          // User already exists but hasn't verified email
          setErrorMessage('An account with this email already exists. Please log in or reset your password.');
        } else if (data?.user && !data?.session) {
          // Email confirmation required
          setSuccessMessage('Please check your email for a confirmation link to complete your registration.');
          setShowResendButton(true);
        } else if (data?.user && data?.session) {
          // Auto-confirmed and logged in
          console.log("Signup successful, redirecting to dashboard");
      navigate('/dashboard');
    }
      }
    } catch (error) {
      console.error("Auth error:", error);
      setErrorMessage(error.message || 'An error occurred during authentication');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleResendConfirmation = async () => {
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) throw error;
      
      setSuccessMessage('Confirmation email resent! Please check your inbox.');
    } catch (error) {
      console.error("Resend confirmation error:", error);
      setErrorMessage(error.message || 'Failed to resend confirmation email');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
            next: '/dashboard'
          }
        }
      });
      
      if (error) throw error;
    } catch (error) {
      console.error("Google auth error:", error);
      setErrorMessage(error.message || 'An error occurred during Google authentication');
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-white via-purple-50/20 to-pink-50/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-xl shadow-lg p-8 glass-panel"
           style={{
             background: 'rgba(255, 255, 255, 0.9)',
             backdropFilter: 'blur(10px)',
             border: '1px solid rgba(255, 255, 255, 0.18)',
             boxShadow: '0 8px 32px rgba(138, 43, 226, 0.1)'
           }}>
        
        {/* Card Header */}
        <div className="space-y-1 text-center mb-6">
          <h1 className="text-2xl font-bold gradient-heading"
              style={{
                background: 'linear-gradient(90deg, #8a2be2 0%, #d946ef 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
            Welcome
          </h1>
          <p className="text-gray-600">
            Sign in or create an account for more personalised recommendations
          </p>
        </div>
        
        {/* Success Message - make it more prominent */}
        {successMessage && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700 font-medium">{successMessage}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Authentication Tabs */}
        <div className="mb-6">
          <div className="grid w-full grid-cols-2 border-b border-gray-200">
            <button 
              className={`py-2.5 text-center transition-colors ${activeTab === 'login' ? 'border-b-2 border-[#8a2be2] text-[#8a2be2] font-medium' : 'text-gray-500'}`}
              onClick={() => {
                setActiveTab('login');
                setErrorMessage('');
                setSuccessMessage('');
                setShowResendButton(false);
              }}
            >
              Login
            </button>
            <button 
              className={`py-2.5 text-center transition-colors ${activeTab === 'signup' ? 'border-b-2 border-[#8a2be2] text-[#8a2be2] font-medium' : 'text-gray-500'}`}
              onClick={() => {
                setActiveTab('signup');
                setErrorMessage('');
                setSuccessMessage('');
                setShowResendButton(false);
              }}
            >
              Sign Up
            </button>
          </div>
        </div>







        {/* Custom Form */}
        <form onSubmit={handleEmailLogin} className="space-y-4">
          {/* Email Field */}
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="youremail@example.com"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8a2be2] focus:border-transparent"
            />
          </div>
          
          {/* Password Field */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <a href="#forgot-password" className="text-xs text-[#8a2be2] hover:underline">
                Forgot password?
              </a>
            </div>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8a2be2] focus:border-transparent"
            />
          </div>
          
          {/* Error Message */}
          {errorMessage && (
            <div className="text-red-500 text-sm py-2">
              {errorMessage}
            </div>
          )}
          
          {/* Login/Signup Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 rounded-md text-white font-medium transition-colors"
            style={{
              background: 'linear-gradient(90deg, #8a2be2 0%, #d946ef 100%)',
              boxShadow: '0 4px 6px rgba(138, 43, 226, 0.25)',
            }}
          >
            {isLoading 
              ? (activeTab === 'login' ? 'Logging in...' : 'Creating account...') 
              : (activeTab === 'login' ? 'Login' : 'Sign Up')}
          </button>
          
          {/* Resend Confirmation Button */}
          {showResendButton && (
            <button
              type="button"
              onClick={handleResendConfirmation}
              disabled={isLoading}
              className="w-full py-2 px-4 rounded-md text-[#8a2be2] font-medium bg-white border border-[#8a2be2]/30 hover:bg-purple-50 transition-colors mt-2"
            >
              {isLoading ? 'Sending...' : 'Resend Confirmation Email'}
            </button>
          )}
        </form>



        {/* Switch Between Login/Signup */}
        <div className="mt-4 text-center text-sm text-gray-600">
          {activeTab === 'login' 
            ? "Don't have an account? " 
            : "Already have an account? "}
          <a 
            href="#switch" 
            onClick={(e) => {
              e.preventDefault();
              setActiveTab(activeTab === 'login' ? 'signup' : 'login');
              setErrorMessage('');
              setSuccessMessage('');
              setShowResendButton(false);
            }}
            className="text-[#8a2be2] hover:underline"
          >
            {activeTab === 'login' ? 'Sign up' : 'Login'}
          </a>
        </div>
        




        {/* Social Login Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-gray-500">OR CONTINUE WITH</span>
          </div>
        </div>
        
        {/* Google Login Button */}
        <button
          onClick={handleGoogleLogin}
          disabled={isLoading}
          className="w-full py-2 px-4 border border-gray-300 rounded-md bg-white text-gray-700 font-medium flex items-center justify-center space-x-2 hover:bg-purple-50 hover:border-[#8a2be2]/40 transition-colors"
        >
          {/* Google icon */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512" className="h-4 w-4">
            <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"/>
          </svg>
          <span>Google</span>
        </button>
   


        {/* Social Login Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-gray-500">OR</span>
          </div>
        </div>

        {/* Skip Login Option */}
        <div className="mt-3 text-center text-sm">
          <a
            href="#skip-login"
            onClick={(e) => {
              e.preventDefault();
              // Set guest mode
              sessionStorage.setItem('guestMode', 'true');
              navigate('/dashboard');
            }}
            className="text-gray-500 hover:text-[#8a2be2] transition-colors"
          >
            Go ahead without logging in →
          </a>
        </div>
        
        {/* Footer with privacy info */}
        {/* <div className="mt-6 text-center text-xs text-gray-500">
          <p>By signing up, you agree to our <a href="#" className="text-[#8a2be2] hover:underline">Terms of Service</a> and <a href="#" className="text-[#8a2be2] hover:underline">Privacy Policy</a>.</p>
        </div> */}
      </div>
    </div>
  );
}

export default Login;